// Compact relative timestamps ("just now", "5m ago", "3h ago", "2d ago"),
// falling back to an absolute date once the gap stops being useful as a
// duration.
//
// This existed twice — once in HomePage, once in AuditLogPage — with the same
// thresholds but different rounding, so the same instant could render as "2m
// ago" on one screen and "1m ago" on another. The self-registration queue would
// have been the third copy. One implementation, three call sites.
//
// Pure and i18n-free: it returns a key (plus `count` for the pluralised ones)
// or an already-formatted absolute date, and the caller resolves it. `nowMs` is
// injectable so tests don't depend on the wall clock.

export type RelativeTime =
  | { key: string; count?: number; absolute?: undefined }
  | { key?: undefined; count?: undefined; absolute: string };

const MINUTE_MS = 60_000;

export function relativeTime(then: Date, nowMs: number = Date.now()): RelativeTime {
  // Clamp: a clock skewed a few seconds ahead of the server must render "just
  // now", never a negative count.
  const diffMs = Math.max(0, nowMs - then.getTime());

  // Floor throughout, so a label never claims more elapsed time than has
  // actually passed — 90 seconds is "1m ago", not "2m ago".
  const mins = Math.floor(diffMs / MINUTE_MS);
  if (mins < 1) return { key: 'common.justNow' };
  if (mins < 60) return { key: 'common.minutesAgo', count: mins };

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return { key: 'common.hoursAgo', count: hrs };

  const days = Math.floor(hrs / 24);
  if (days < 7) return { key: 'common.daysAgo', count: days };

  return { absolute: then.toLocaleDateString() };
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Resolve `relativeTime` to display text. */
export function formatRelativeTime(
  then: Date,
  t: Translate,
  nowMs: number = Date.now(),
): string {
  const r = relativeTime(then, nowMs);
  if (r.absolute !== undefined) return r.absolute;
  return r.count === undefined ? t(r.key) : t(r.key, { count: r.count });
}
